import { useAgencyCampaignContext } from '../agencyCampaignContext';
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

import {
  creativesRouteSearchSchema,
  parseCreativesRouteSearch,
  serializeCreativesRouteSearch,
  type CreativesRouteFilters,
} from './creativesRouteSearch';
import { CreativeTab, defaultCreativesFilter, type CreativeStatus } from './model';

export function useCreativesRouteFilters(tab: CreativeTab) {
  const listRoute =
    tab === CreativeTab.Display ? '/campaigns/creatives/display' : '/campaigns/creatives/video';
  const scope = useAgencyCampaignContext();
  const navigate = useNavigate({ from: scope ? '/a/$agencyId/campaigns/$section' : listRoute });
  const search = useSearch({ strict: false });
  const routeSearch = useMemo(() => creativesRouteSearchSchema(search), [search]);
  const filters = useMemo(() => parseCreativesRouteSearch(routeSearch, tab), [routeSearch, tab]);
  const updateFilters = useCallback(
    (updater: (current: CreativesRouteFilters) => CreativesRouteFilters) => {
      void navigate({
        search: (current) => ({
          ...(scope ? { creativeTab: tab } : {}),
          ...serializeCreativesRouteSearch(
            updater(parseCreativesRouteSearch(creativesRouteSearchSchema(current), tab)),
            tab,
          ),
        }),
        replace: true,
      });
    },
    [navigate, scope, tab],
  );

  return {
    setAgencies: (agencyIds: string[]) => {
      updateFilters((current) => ({ ...current, agencyIds, page: 0 }));
    },
    filters,
    queryFilter: { ...defaultCreativesFilter(tab), ...filters },
    setBrand: useCallback(
      (brandId: string) => {
        updateFilters((current) => ({ ...current, brandId, page: 0 }));
      },
      [updateFilters],
    ),
    setSearch: useCallback(
      (name: string) => {
        updateFilters((current) => ({ ...current, name, page: 0 }));
      },
      [updateFilters],
    ),
    setStatuses: useCallback(
      (activeStatus: CreativeStatus[]) => {
        updateFilters((current) => ({ ...current, activeStatus, page: 0 }));
      },
      [updateFilters],
    ),
    setPagination: useCallback(
      (page: number, size: number) => {
        updateFilters((current) => ({ ...current, page, size }));
      },
      [updateFilters],
    ),
  };
}
